import axios from 'axios';
import { FILESTORE_API_URL } from '../lib/environment.js';

import { newAbortSignalFunc } from './modules/api.js';
import entries from './entries.js';

const timeoutAmount = 180000;

// Session ID for temp uploads — unique per page load, shared across both email editor contexts
const SESSION_ID = `${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
const TEMP_SESSION_KEY = 'designAssetsTempSession';
const TEMP_URL_PREFIX = /design\/assets\/temp\/[^/]+\//g;
const OUTPUT_TEMP_URL_PREFIX = /design\/output\/temp\/[^/]+\//;

/** Thrown when moving temp uploads into the template folder fails; the temp session is left intact. */
export class MigrationError extends Error {
	constructor(message, { templateId, keys, cause } = {}) {
		super(message);
		this.name = 'MigrationError';
		this.templateId = templateId;
		this.keys = keys;
		this.cause = cause;
	}
}

// entries.move resolves with the response body. The relocation job answers
// `message: 'OK'` (BrandAssets / ReadyToShare rely on it); `status: 0` is the
// upload-style shape. Either is success; anything else is a failed move.
async function moveOrThrow(keys, partition, dstPath, templateId) {
	let resp;
	try {
		resp = await entries.move(keys, partition, dstPath);
	} catch (err) {
		throw new MigrationError('Failed to move temp images', {
			templateId,
			keys,
			cause: err,
		});
	}
	if (resp?.status !== 0 && resp?.message !== 'OK') {
		throw new MigrationError('Failed to move temp images', {
			templateId,
			keys,
			cause: resp,
		});
	}
}

/**
 * Get or create the temp session store in sessionStorage.
 * Tracks { entryId, tempUrl, fileName } for each temp-uploaded file.
 * @returns {{ sessionId: string, files: Array<{entryId: string, tempUrl: string, fileName: string}> }}
 */
function getTempSession() {
	try {
		const raw = window.sessionStorage.getItem(TEMP_SESSION_KEY);
		if (raw) return JSON.parse(raw);
	} catch (_) {
		/* ignore */
	}
	return { sessionId: SESSION_ID, files: [] };
}

function saveTempSession(session) {
	try {
		window.sessionStorage.setItem(TEMP_SESSION_KEY, JSON.stringify(session));
	} catch (_) {
		/* ignore */
	}
}

function clearTempSession() {
	try {
		window.sessionStorage.removeItem(TEMP_SESSION_KEY);
	} catch (_) {
		/* ignore */
	}
}

// Any session id: a preview recorded under another route's id must still land in design-output/<templateId>.
function isPreviewTempAssetUrl(tempUrl) {
	return typeof tempUrl === 'string' && OUTPUT_TEMP_URL_PREFIX.test(tempUrl);
}

export function remapTempAssetUrl(src, templateId) {
	return typeof src === 'string' && templateId
		? src.replace(TEMP_URL_PREFIX, `design/assets/${templateId}/`)
		: src;
}

function remapTempOutputUrl(src, templateId) {
	return src.replace(OUTPUT_TEMP_URL_PREFIX, `design/output/${templateId}/`);
}

/**
 * Upload a file to the given filestore partition under the given path prefix.
 * Returns { url, entryId } from the filestore response.
 */
function uploadToFilestore(file, path, partition) {
	return new Promise((resolve, reject) => {
		const formData = new FormData();
		formData.append('file', file);
		const transformFormat = file?.type === 'image/png' ? 'png' : 'jpeg';

		axios({
			url: `${FILESTORE_API_URL}/partitions/${partition}/entries$File`,
			method: 'POST',
			data: formData,
			params: {
				path,
				onConflict: 'rename',
				editor_type: 'email',
				// Backend supports overriding image output shape via transforms.image.format.
				// Keep png for transparency; default jpeg for non-png uploads.
				'transforms.image.format': JSON.stringify({ type: transformFormat }),
			},
			withCredentials: true,
			timeout: timeoutAmount,
			signal: newAbortSignalFunc(timeoutAmount),
		})
			.then((resp) => {
				if (resp.data && resp.data.status === 0) {
					resolve({ url: resp.data.result.url, entryId: resp.data.result._id });
				} else {
					reject(new Error(resp.data?.message || 'Upload failed'));
				}
			})
			.catch(reject);
	});
}

function readEntries(designId) {
	if (!designId) {
		return Promise.resolve(null);
	}

	return new Promise((resolve, reject) => {
		const controller = new AbortController();

		let timeout = setTimeout(() => {
			controller.abort();
		}, timeoutAmount);

		axios({
			url: `${FILESTORE_API_URL}/partitions/design-assets/entries`,
			method: 'GET',
			cache: 'no-cache',
			withCredentials: true,
			params: {
				glob: `${designId}/**.*`,
			},
			signal: controller.signal,
		})
			.then((resp) => {
				clearTimeout(timeout);
				resolve(resp.data);
			})
			.catch((err) => {
				clearTimeout(timeout);
				reject(err);
			});
	});
}

// Filename is the last path segment, minus any ?query.
const fileNameOf = (url) =>
	String(url || '')
		.split('?')[0]
		.split('/')
		.pop();

const TEMP_URL_IN_PAYLOAD =
	/https?:\/\/[^"'\s]*?design\/assets\/temp\/[^/"'\s]+\/[^"'\s]+?(?=["'\s])/g;

const designAssets = () => {
	return {
		readEntriesByDesignId: readEntries,

		// MARK: Upload
		/**
		 * Upload an image for the email editor.
		 *
		 * - If templateId is provided (editing): upload directly to `<templateId>/<filename>` and return the permanent URL.
		 * - If no templateId (creating): upload to `temp/<sessionId>/<filename>`, track the entry for migration on save,
		 *   and return the temp URL immediately so the preview works right away.
		 *
		 * @param {File|Blob} file
		 * @param {string|null} templateId
		 * @param {{ previewVideoUpload?: boolean }} options  previewVideoUpload: the rendered template
		 *   video, stored in design-output so it outlives the template.
		 * @returns {Promise<string>} filestore URL
		 */
		upload: async (file, templateId, options = {}) => {
			const previewVideoUpload = Boolean(options?.previewVideoUpload);
			const partition = previewVideoUpload ? 'design-output' : 'design-assets';
			const timestamp = Date.now();
			const randomId = Math.random().toString(36).substring(2, 10);
			const fileExtension =
				file instanceof File ? file.name.split('.').pop() || 'png' : 'png';
			const fileName = `${timestamp}-${randomId}.${fileExtension}`;

			if (!templateId || templateId === 'create') {
				// No template yet — upload to temp path so we get a real URL immediately.
				// These will be moved to <templateId>/ when the template is first saved.
				const tempPath = `temp/${getTempSession().sessionId}/${fileName}`;

				const { url, entryId } = await uploadToFilestore(
					file,
					tempPath,
					partition,
				);

				// Re-read after the await: parallel uploads each save in turn, so a
				// session read before the await would drop the others' entries.
				const session = getTempSession();
				if (!Array.isArray(session.files)) session.files = [];
				session.files.push({ entryId, tempUrl: url, fileName });
				saveTempSession(session);

				return url;
			}

			// Editing an existing template — upload directly to its folder.
			const path = `${templateId}/${fileName}`;
			const { url } = await uploadToFilestore(file, path, partition);
			return url;
		},

		/**
		 * Move all temp-uploaded files to <templateId>/ and replace their URLs in the template data.
		 * Call this immediately after a new template is created and you have its ID.
		 *
		 * @param {string} templateId  - The newly-created template ID
		 * @param {any} templateData   - The full template JSON/object to patch URLs in
		 * @returns {Promise<any>}     - Updated template data with permanent URLs
		 */
		migrateTempImages: async (templateId, templateData) => {
			const session = getTempSession();
			const allFiles = Array.isArray(session.files) ? session.files : [];
			// Entries without an id cannot be moved; one bad key would fail every save.
			const sessionFiles = allFiles.filter((file) => file?.entryId != null);
			if (sessionFiles.length < allFiles.length) {
				console.warn(
					`Skipping ${allFiles.length - sessionFiles.length} temp upload(s) with no entry id`,
				);
			}

			if (!sessionFiles.length) {
				clearTempSession();
				return templateData;
			}

			const { previewVideoFiles, regularFiles } = sessionFiles.reduce(
				(acc, file) => {
					if (isPreviewTempAssetUrl(file?.tempUrl)) {
						acc.previewVideoFiles.push(file);
					} else {
						acc.regularFiles.push(file);
					}
					return acc;
				},
				{ previewVideoFiles: [], regularFiles: [] },
			);
			if (regularFiles.length > 0) {
				await moveOrThrow(
					regularFiles.map((file) => file.entryId),
					'design-assets',
					templateId,
					templateId,
				);
			}

			if (previewVideoFiles.length > 0) {
				await moveOrThrow(
					previewVideoFiles.map((file) => file.entryId),
					'design-output',
					templateId,
					templateId,
				);
			}

			clearTempSession();
			if (templateData == null) return templateData;

			// Rewrite only what was actually moved; other temp URLs point at files
			// that still live under temp/.
			let dataStr = JSON.stringify(templateData);
			sessionFiles.forEach(({ tempUrl }) => {
				if (typeof tempUrl !== 'string') return;
				const newUrl = isPreviewTempAssetUrl(tempUrl)
					? remapTempOutputUrl(tempUrl, templateId)
					: remapTempAssetUrl(tempUrl, templateId);
				dataStr = dataStr.split(tempUrl).join(newUrl);
			});
			return JSON.parse(dataStr);
		},

		/**
		 * Duplicate all design assets from one template to another.
		 * Call this when duplicating a template to copy its associated images.
		 *
		 * @param {string} sourceTemplateId - The original template ID to copy assets from
		 * @param {string} targetTemplateId - The new template ID to copy assets to
		 * @param {any} templateData        - The duplicated template JSON/object to patch URLs in
		 * @returns {Promise<any>}          - Updated template data with new asset URLs
		 */
		duplicateTemplateAssets: async (
			sourceTemplateId,
			targetTemplateId,
			templateData,
		) => {
			try {
				// Use entries.move with isCopy=true to duplicate all entries in the source folder
				// This will copy all files from design-assets/<sourceTemplateId>/ to design-assets/<targetTemplateId>/
				const result = await entries.move(
					[sourceTemplateId], // Copy the entire folder by specifying the folder key/path
					'design-assets',
					targetTemplateId,
					true, // isCopy = true for duplication
				);

				// Update URLs in template data from old template ID to new template ID
				// e.g. https://cdn.../design-assets/<sourceTemplateId>/file.png
				//   →  https://cdn.../design-assets/<targetTemplateId>/file.png
				let dataStr = JSON.stringify(templateData);
				const oldPath = `design-assets/${sourceTemplateId}/`;
				const newPath = `design-assets/${targetTemplateId}/`;
				dataStr = dataStr.split(oldPath).join(newPath);

				return JSON.parse(dataStr);
			} catch (error) {
				// If no assets exist for the source template, that's okay - just return unchanged data
				if (
					error?.response?.status === 404 ||
					error?.message?.includes('not found')
				) {
					return templateData;
				}
				// For other errors, log but don't fail the duplication
				console.error('⚠️  Error duplicating template assets:', error);
				return templateData;
			}
		},

		/**
		 * Temp URLs of uploads still waiting to be moved. Read this BEFORE
		 * migrateTempImages so the host knows which canvas srcs to re-point after.
		 */
		getPendingTempUrls: () => {
			const files = getTempSession().files;
			return Array.isArray(files)
				? files
						.filter((file) => file?.entryId != null)
						.map((file) => file.tempUrl)
				: [];
		},

		/**
		 * Heal temp URLs whose files were already moved server-side (e.g. a failed
		 * save that moved them anyway). Only URLs whose filename now exists under
		 * <templateId>/ are re-pointed. Call only while no temp files are pending.
		 *
		 * @returns {Promise<{ data: any, remapped: Record<string, string> }>}
		 */
		remapMovedTempUrls: async (templateId, data) => {
			if (!templateId || data == null) return { data, remapped: {} };
			const dataStr = JSON.stringify(data);
			const tempUrls = [...new Set(dataStr.match(TEMP_URL_IN_PAYLOAD) || [])];
			if (!tempUrls.length) return { data, remapped: {} };

			const moved = new Set();
			try {
				const resp = await readEntries(templateId);
				const result = resp?.result;
				(Array.isArray(result) ? result : result ? [result] : []).forEach(
					(entry) => {
						const name = fileNameOf(entry?.name || entry?.url);
						if (name) moved.add(name);
					},
				);
			} catch (err) {
				console.warn('Failed to list design assets for temp URL healing:', err);
				return { data, remapped: {} };
			}

			const remapped = {};
			let nextStr = dataStr;
			tempUrls.forEach((tempUrl) => {
				if (!moved.has(fileNameOf(tempUrl))) return;
				const newUrl = tempUrl.replace(
					TEMP_URL_PREFIX,
					`design/assets/${templateId}/`,
				);
				remapped[tempUrl] = newUrl;
				nextStr = nextStr.split(tempUrl).join(newUrl);
			});
			return Object.keys(remapped).length
				? { data: JSON.parse(nextStr), remapped }
				: { data, remapped };
		},

		/** True while temp uploads are waiting to be moved (so their temp URLs are still valid). */
		hasPendingTempFiles: () => {
			const files = getTempSession().files;
			return Array.isArray(files) && files.length > 0;
		},

		/**
		 * Clear the temp session without migrating (e.g. user navigated away without saving).
		 * The backend can sweep the temp/ directory on a schedule.
		 */
		clearTempSession,
	};
};

const singleton = designAssets();
Object.freeze(singleton);

export default singleton;
